import { Accessor, Show } from 'solid-js';
import { FieldType, FormFieldContext } from '../forms-types';
import { errorDescribedBy, FieldErrorMessage, fieldErrorId, FormFieldLabel, FormInput, FormTextarea } from './primitives';
import { getUniqueSlug } from '../utils';

type FieldProps = {
    field: FieldType;
    ctx: FormFieldContext;
};

type TextInputProps = FieldProps & {
    forceRequire?: Accessor<boolean>;
};

const slugTranslationOverride: Record<string, string> = {
    firstname: 'first-name',
    lastname: 'last-name',
    zip: 'postal-code',
};

const inputAutocompleteBySlug: Record<string, { autocomplete: string; type?: string }> = {
    email: { autocomplete: 'email', type: 'email' },
    phone: { autocomplete: 'tel', type: 'tel' },
    company: { autocomplete: 'organization' },
};

export const TextInput = (props: TextInputProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const required = () => !!(props.field.required || props.forceRequire?.());
    const translationKey = () => slugTranslationOverride[slug()] ?? slug();
    const autocompleteEntry = () => inputAutocompleteBySlug[slug()];
    const placeholder = () => localize(translationKey(), props.field.name);
    const label = () => `${placeholder()}${required() ? '*' : ''}`;
    const inputId = () => `${formId}-${slug()}`;

    return (
        <div>
            <FormFieldLabel for={inputId()}>
                {label()}
            </FormFieldLabel>

            <FormInput
                id={inputId()}
                required={required()}
                value={formValues()[slug()]}
                onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                placeholder={placeholder()}
                hasErrors={!!errors()[slug()]}
                name={slug()}
                aria-describedby={errorDescribedBy(inputId(), !!errors()[slug()])}
                {...autocompleteEntry()?.autocomplete ? { autocomplete: autocompleteEntry()!.autocomplete } : {}}
                type={autocompleteEntry()?.type}
            />

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} id={fieldErrorId(inputId())} />
            </Show>
        </div>
    );
};

// Custom WP text field: slug/name/eloqua-value comes from the editor-supplied
// extraSettings.name.value, not the field slug.
export const TextField = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    // The editor's "placeholder" setting is both the label and the placeholder text.
    const placeholder = () => extraSettings()?.placeholder?.value || localize('text-field', 'Text field');
    const label = () => placeholder() + (props.field.required ? '*' : '');
    const eloquaName = () => extraSettings()?.name.value ?? '';
    const uniqueSlug = () => getUniqueSlug(props.field);
    const inputId = () => `${formId}-${uniqueSlug()}`;

    return (
        <Show when={extraSettings()}>
            <div>
                <FormFieldLabel for={inputId()}>
                    {label()}
                </FormFieldLabel>

                <FormInput
                    id={inputId()}
                    name={'custom-text-field-' + eloquaName()}
                    required={props.field.required}
                    value={formValues()[uniqueSlug()]}
                    onChange={(e) => onInputChange(eloquaName(), e.currentTarget.value, props.field.slug)}
                    placeholder={placeholder()}
                    hasErrors={!!errors()[uniqueSlug()]}
                    aria-describedby={errorDescribedBy(inputId(), !!errors()[uniqueSlug()])}
                />

                <Show when={errors()[uniqueSlug()]}>
                    <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} id={fieldErrorId(inputId())} />
                </Show>
            </div>
        </Show>
    );
};

export const TextArea = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    // The editor's "placeholder" setting is both the label and the placeholder text.
    const placeholder = () => props.field.extraSettings?.placeholder?.value || localize('text-area', 'Text area');
    const label = () => placeholder() + (props.field.required ? '*' : '');
    const uniqueSlug = () => getUniqueSlug(props.field);
    const inputId = () => `${formId}-${uniqueSlug()}`;

    return (
        <div>
            <FormFieldLabel for={inputId()}>
                {label()}
            </FormFieldLabel>

            <FormTextarea
                id={inputId()}
                required={props.field.required}
                value={formValues()[uniqueSlug()]}
                onChange={(e) => onInputChange(uniqueSlug(), e.currentTarget.value, props.field.slug)}
                placeholder={placeholder()}
                name={'custom-text-area-' + props.field.slug}
                rows={6}
                aria-describedby={errorDescribedBy(inputId(), !!errors()[uniqueSlug()])}
                hasErrors={!!errors()[uniqueSlug()]}
            />

            <Show when={errors()[uniqueSlug()]}>
                <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} id={fieldErrorId(inputId())} />
            </Show>
        </div>
    );
};

export const Comment = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    const uniqueSlug = () => getUniqueSlug(props.field);
    const inputId = () => `${formId}-${uniqueSlug()}`;

    return (
        <div>
            <FormFieldLabel for={inputId()}>
                {`${localize('comments', 'Comments')}${props.field.required ? '*' : ''}`}
            </FormFieldLabel>

            <FormTextarea
                id={inputId()}
                required={props.field.required}
                value={formValues()[uniqueSlug()]}
                onChange={(e) => onInputChange(uniqueSlug(), e.currentTarget.value)}
                placeholder={localize('comments', 'Comments')}
                name={uniqueSlug()}
                rows={1}
                aria-describedby={errorDescribedBy(inputId(), !!errors()[uniqueSlug()])}
                hasErrors={!!errors()[uniqueSlug()]}
            />

            <Show when={errors()[uniqueSlug()]}>
                <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} id={fieldErrorId(inputId())} />
            </Show>
        </div>
    );
};
